import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { supabase } from '../../supabase/config';
import { inventoryService } from '../../services/inventoryService';
import {
    Package, Search, Save, AlertCircle, RefreshCw, Layers, TrendingDown,
    CheckCircle2, Filter, ArrowUpDown, Minus, Plus, AlertTriangle,
    ChevronLeft, ChevronRight, Settings2, X, History, BarChart3, Infinity as InfinityIcon
} from 'lucide-react';

const ITEMS_PER_PAGE = 25;
const DEFAULT_CRITICAL_THRESHOLD = 10;

const Inventory = () => {
    const { t } = useTranslation();
    const { effectiveTenantId, tenantData } = useAuth();
    const { showAlert, showConfirm } = useAlert();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);
    const [inventoryData, setInventoryData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [stockMenuEnabled, setStockMenuEnabled] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('name'); // name, stock-asc, stock-desc, category
    const [currentPage, setCurrentPage] = useState(1);
    const [criticalThreshold, setCriticalThreshold] = useState(DEFAULT_CRITICAL_THRESHOLD);
    const [showSettings, setShowSettings] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [stockLog, setStockLog] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [savedData, setSavedData] = useState({});

    useEffect(() => {
        if (tenantData) {
            setStockMenuEnabled(!!tenantData.stock_menu_enabled);
        }
    }, [tenantData]);

    useEffect(() => {
        if (!effectiveTenantId) return;
        let unsubStocks = null;

        const loadMenuAndSyncStocks = async () => {
            setLoading(true);
            try {
                const { data: mDoc } = await supabase
                    .from('menus')
                    .select('*')
                    .eq('tenant_id', effectiveTenantId)
                    .maybeSingle();
                let cats = [];
                if (mDoc) {
                    const rawCategories = mDoc.categories_json || mDoc.categoriesJson || mDoc.categories || mDoc.Categories || [];
                    if (typeof rawCategories === 'string') {
                        try {
                            const parsed = JSON.parse(rawCategories);
                            cats = Array.isArray(parsed) ? parsed : Object.values(parsed);
                        } catch (e) { console.error("Menu Parse Error", e); }
                    } else {
                        cats = Array.isArray(rawCategories) ? rawCategories : Object.values(rawCategories);
                    }
                }

                const flattenedProducts = [];
                const seenIds = new Set();
                cats.forEach(cat => {
                    const items = typeof cat.items === 'object' ? Object.values(cat.items || {}) : (Array.isArray(cat.items) ? cat.items : []);
                    items.forEach(item => {
                        const prodId = `${cat.categoryName}_${item.name}`;
                        if (!seenIds.has(prodId)) {
                            flattenedProducts.push({
                                id: prodId, name: item.name,
                                category: cat.categoryName,
                                image: item.image || item.imageUrl || null,
                                price: item.price || null,
                            });
                            seenIds.add(prodId);
                        }
                    });
                });
                setProducts(flattenedProducts);

                unsubStocks = inventoryService.subscribeToStocks(effectiveTenantId, (stocks) => {
                    setInventoryData(stocks);
                    setSavedData(stocks);
                    setLoading(false);
                });
            } catch (err) {
                console.error("Inventory Load Error:", err);
                setLoading(false);
            }
        };

        loadMenuAndSyncStocks();
        return () => unsubStocks && unsubStocks();
    }, [effectiveTenantId]);

    // Track changes
    useEffect(() => {
        const changed = JSON.stringify(inventoryData) !== JSON.stringify(savedData);
        setHasChanges(changed);
    }, [inventoryData, savedData]);

    const categories = useMemo(() => {
        const cats = [...new Set(products.map(p => p.category))].sort();
        return ['all', ...cats];
    }, [products]);

    const handleUpdateStock = (prodId, value) => {
        const newVal = value === -1 ? -1 : Math.max(0, parseInt(value) || 0);
        setInventoryData(prev => ({ ...prev, [prodId]: newVal }));

        // Log the change
        const prod = products.find(p => p.id === prodId);
        const oldVal = savedData[prodId] || 0;
        if (newVal !== oldVal) {
            setStockLog(prev => [{
                productName: prod?.name || prodId,
                category: prod?.category || '',
                oldStock: oldVal === -1 ? '∞' : oldVal,
                newStock: newVal === -1 ? '∞' : newVal,
                change: newVal === -1 ? '∞' : (oldVal === -1 ? newVal : newVal - oldVal),
                timestamp: new Date(),
            }, ...prev.slice(0, 99)]);
        }
    };

    const handleToggleUnlimited = (prodId) => {
        const current = inventoryData[prodId] || 0;
        handleUpdateStock(prodId, current === -1 ? 0 : -1);
    };

    const handleQuickSet = (prodId, value) => {
        handleUpdateStock(prodId, value);
    };

    const handleToggleStockIntegration = async () => {
        if (!effectiveTenantId) return;
        const newValue = !stockMenuEnabled;
        setStockMenuEnabled(newValue);
        try {
            const { error } = await supabase
                .from('tenants')
                .update({ stock_menu_enabled: newValue })
                .eq('id', effectiveTenantId);
            if (error) throw error;
        } catch (err) {
            console.error("Error updating stock integration:", err);
            setStockMenuEnabled(!newValue);
        }
    };

    const handleSave = async () => {
        if (!effectiveTenantId) return;

        const confirmed = await showConfirm('Stok değişikliklerini kaydetmek istediğinize emin misiniz?', {
            title: 'Stokları Kaydet',
            confirmText: 'Kaydet',
            type: 'info'
        });
        if (!confirmed) return;

        setSaving(true);
        try {
            const activeIds = new Set(products.map(p => p.id));
            const cleanedStocks = {};
            Object.keys(inventoryData).forEach(id => {
                if (activeIds.has(id)) cleanedStocks[id] = inventoryData[id];
            });
            await inventoryService.saveStocks(effectiveTenantId, cleanedStocks);
            setSavedData(cleanedStocks);
            setHasChanges(false);
            showAlert('Stoklar kaydedildi.', 'success');
        } catch (err) {
            showAlert(t('common.error'), 'error');
        } finally { setSaving(false); }
    };

    const handleBulkSetCategory = (category, value) => {
        const catProducts = products.filter(p => category === 'all' || p.category === category);
        const newData = { ...inventoryData };
        catProducts.forEach(p => { newData[p.id] = Math.max(0, parseInt(value) || 0); });
        setInventoryData(newData);
    };

    // Filtered & sorted products
    const filteredProducts = useMemo(() => {
        let result = products.filter(p =>
            (selectedCategory === 'all' || p.category === selectedCategory) &&
            (p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.category.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const stockVal = (id) => { const s = inventoryData[id] || 0; return s === -1 ? Infinity : s; };
        switch (sortBy) {
            case 'stock-asc':
                result.sort((a, b) => stockVal(a.id) - stockVal(b.id));
                break;
            case 'stock-desc':
                result.sort((a, b) => stockVal(b.id) - stockVal(a.id));
                break;
            case 'category':
                result.sort((a, b) => a.category.localeCompare(b.category));
                break;
            default:
                result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        }
        return result;
    }, [products, inventoryData, searchTerm, selectedCategory, sortBy]);

    // Pagination
    const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    const paginatedProducts = filteredProducts.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedCategory, sortBy]);

    // Stats
    const stats = useMemo(() => {
        const total = products.length;
        const unlimited = products.filter(p => inventoryData[p.id] === -1).length;
        const outOfStock = products.filter(p => (inventoryData[p.id] || 0) === 0).length;
        const critical = products.filter(p => {
            const s = inventoryData[p.id] || 0;
            return s > 0 && s < criticalThreshold;
        }).length;
        const healthy = total - outOfStock - critical;
        return { total, outOfStock, critical, healthy, unlimited };
    }, [products, inventoryData, criticalThreshold]);

    const getStockStatus = (stock) => {
        if (stock === -1) return { label: 'Sınırsız', color: '#6366f1', bg: '#eef2ff', icon: InfinityIcon };
        if (stock === 0) return { label: 'Stokta Yok', color: '#ef4444', bg: '#fef2f2', icon: AlertCircle };
        if (stock < criticalThreshold) return { label: 'Kritik', color: '#f59e0b', bg: '#fffbeb', icon: AlertTriangle };
        return { label: 'Yeterli', color: '#22c55e', bg: '#f0fdf4', icon: CheckCircle2 };
    };

    const getStockBarWidth = (stock) => {
        if (stock === -1) return 100;
        const max = Math.max(...products.map(p => { const s = inventoryData[p.id] || 0; return s === -1 ? 0 : s; }), 1);
        return Math.min(100, (stock / max) * 100);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
                <RefreshCw className="animate-spin" size={32} color="var(--primary)" />
                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Stok verileri yükleniyor...</span>
            </div>
        );
    }

    return (
        <div className="fade-in">
            {/* Header */}
            <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
                        {t('nav.inventory')}
                    </h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                        Ürünlerinizin stok durumunu takip edin ve güncelleyin.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Stock Log */}
                    {stockLog.length > 0 && (
                        <button
                            onClick={() => setShowLogModal(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-card)',
                                color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600,
                                fontSize: '0.85rem',
                            }}
                        >
                            <History size={18} />
                            <span>{stockLog.length}</span>
                        </button>
                    )}

                    {/* Sync Toggle */}
                    <button
                        onClick={handleToggleStockIntegration}
                        style={{
                            padding: '10px 18px', borderRadius: '12px',
                            display: 'flex', alignItems: 'center', gap: '10px',
                            border: stockMenuEnabled ? '1px solid var(--primary)' : '1px solid var(--border)',
                            background: stockMenuEnabled ? 'rgba(var(--primary-rgb), 0.06)' : 'var(--bg-card)',
                            color: stockMenuEnabled ? 'var(--primary)' : 'var(--text-main)',
                            cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                            transition: 'all 0.2s',
                        }}
                    >
                        <RefreshCw size={16} style={{
                            transition: 'transform 0.5s ease',
                            transform: stockMenuEnabled ? 'rotate(180deg)' : 'rotate(0)',
                        }} />
                        Menü Senk: {stockMenuEnabled ? 'Açık' : 'Kapalı'}
                    </button>

                    {/* Settings */}
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        style={{
                            padding: '10px', borderRadius: '12px',
                            border: '1px solid var(--border)', background: 'var(--bg-card)',
                            color: 'var(--text-muted)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Ayarlar"
                    >
                        <Settings2 size={18} />
                    </button>

                    {/* Save */}
                    <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="btn-primary"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 24px', borderRadius: '12px', fontWeight: 700,
                            fontSize: '0.9rem',
                            opacity: (saving || !hasChanges) ? 0.5 : 1,
                            position: 'relative',
                        }}
                    >
                        {saving ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                        {hasChanges ? 'Kaydet' : 'Kaydedildi'}
                        {hasChanges && (
                            <span style={{
                                position: 'absolute', top: '-4px', right: '-4px',
                                width: '10px', height: '10px', borderRadius: '50%',
                                background: '#ef4444', border: '2px solid var(--bg-card)',
                            }} />
                        )}
                    </button>
                </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
                <div className="card glass" style={{
                    padding: '1.25rem', marginBottom: '1.5rem',
                    border: '1px solid rgba(var(--primary-rgb), 0.15)',
                    background: 'rgba(var(--primary-rgb), 0.02)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Settings2 size={16} /> Stok Ayarları
                        </h3>
                        <button onClick={() => setShowSettings(false)} style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-muted)',
                            transition: 'all 0.2s',
                            outline: 'none'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        >
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Kritik Stok Eşiği
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="number"
                                    min="1"
                                    value={criticalThreshold}
                                    onChange={e => setCriticalThreshold(Math.max(1, parseInt(e.target.value) || 1))}
                                    style={{
                                        width: '80px', height: '40px', textAlign: 'center',
                                        background: 'var(--bg-main)', border: '1px solid var(--border)',
                                        borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem',
                                    }}
                                />
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>adet altı kritik sayılır</span>
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Toplu İşlem ({selectedCategory === 'all' ? 'Tümü' : selectedCategory})
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[0, 10, 25, 50, 100].map(val => (
                                    <button
                                        key={val}
                                        onClick={() => handleBulkSetCategory(selectedCategory, val)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '8px',
                                            border: '1px solid var(--border)', background: 'var(--bg-card)',
                                            color: 'var(--text-main)', cursor: 'pointer',
                                            fontWeight: 600, fontSize: '0.8rem',
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-main)'; }}
                                    >
                                        {val === 0 ? 'Sıfırla' : val}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stat Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
            }}>
                {/* Total Products */}
                <div className="card glass" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)',
                            padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Layers size={22} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toplam Ürün</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{stats.total}</div>
                        </div>
                    </div>
                </div>

                {/* Healthy */}
                <div className="card glass" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#f0fdf4', color: '#22c55e', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircle2 size={22} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Yeterli Stok</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{stats.healthy}</span>
                                {stats.total > 0 && <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#22c55e' }}>%{Math.round((stats.healthy / stats.total) * 100)}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Critical */}
                <div className="card glass" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#fffbeb', color: '#f59e0b', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AlertTriangle size={22} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kritik Seviye</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: stats.critical > 0 ? '#f59e0b' : 'var(--text-main)' }}>{stats.critical}</span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>&lt;{criticalThreshold} adet</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Out of Stock */}
                <div className="card glass" style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#fef2f2', color: '#ef4444', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AlertCircle size={22} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stokta Yok</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: stats.outOfStock > 0 ? '#ef4444' : 'var(--text-main)' }}>{stats.outOfStock}</span>
                                {stats.total > 0 && stats.outOfStock > 0 && <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#ef4444' }}>%{Math.round((stats.outOfStock / stats.total) * 100)}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
                }}>
                    {/* Search */}
                    <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            placeholder="Ürün veya kategori ara..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                paddingLeft: '38px', width: '100%', height: '40px',
                                background: 'var(--bg-main)', border: '1px solid var(--border)',
                                borderRadius: '10px', fontSize: '0.85rem', outline: 'none',
                                color: 'var(--text-main)',
                            }}
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} style={{
                                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                                padding: '2px',
                            }}>
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Category Filter */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Filter size={14} style={{ color: 'var(--text-muted)' }} />
                        <select
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                            style={{
                                height: '40px', padding: '0 12px',
                                background: 'var(--bg-main)', border: '1px solid var(--border)',
                                borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                                color: 'var(--text-main)', cursor: 'pointer', outline: 'none',
                            }}
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat === 'all' ? 'Tüm Kategoriler' : cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Sort */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <ArrowUpDown size={14} style={{ color: 'var(--text-muted)' }} />
                        <select
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                            style={{
                                height: '40px', padding: '0 12px',
                                background: 'var(--bg-main)', border: '1px solid var(--border)',
                                borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                                color: 'var(--text-main)', cursor: 'pointer', outline: 'none',
                            }}
                        >
                            <option value="name">Ada Göre</option>
                            <option value="stock-asc">Stok (Az → Çok)</option>
                            <option value="stock-desc">Stok (Çok → Az)</option>
                            <option value="category">Kategoriye Göre</option>
                        </select>
                    </div>

                    {/* Result count */}
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                        {filteredProducts.length} ürün
                    </span>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                                <th style={{ textAlign: 'left', padding: '0.85rem 1.5rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Ürün</th>
                                <th style={{ textAlign: 'left', padding: '0.85rem 1rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Kategori</th>
                                <th style={{ textAlign: 'center', padding: '0.85rem 1rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', width: '260px' }}>Stok Adedi</th>
                                <th style={{ textAlign: 'left', padding: '0.85rem 1rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', width: '140px' }}>Seviye</th>
                                <th style={{ textAlign: 'center', padding: '0.85rem 1rem', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedProducts.map((prod, idx) => {
                                const stock = inventoryData[prod.id] || 0;
                                const status = getStockStatus(stock);
                                const StatusIcon = status.icon;
                                const barWidth = getStockBarWidth(stock);

                                return (
                                    <tr key={prod.id} style={{
                                        borderBottom: '1px solid var(--border)',
                                        background: stock === -1 ? 'rgba(99, 102, 241, 0.02)' : (stock === 0 ? 'rgba(239, 68, 68, 0.02)' : (stock < criticalThreshold ? 'rgba(245, 158, 11, 0.02)' : 'transparent')),
                                        transition: 'background 0.15s',
                                    }}>
                                        {/* Product */}
                                        <td style={{ padding: '0.85rem 1.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                {prod.image ? (
                                                    <img src={prod.image} alt={prod.name} style={{
                                                        width: '40px', height: '40px', borderRadius: '10px',
                                                        objectFit: 'cover', border: '1px solid var(--border)',
                                                    }} />
                                                ) : (
                                                    <div style={{
                                                        width: '40px', height: '40px', borderRadius: '10px',
                                                        background: 'var(--bg-main)', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center',
                                                        border: '1px solid var(--border)',
                                                    }}>
                                                        <Package size={18} color="var(--text-muted)" style={{ opacity: 0.5 }} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{prod.name}</div>
                                                    {prod.price && (
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                                            {parseFloat(prod.price).toFixed(2)} ₺
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Category */}
                                        <td style={{ padding: '0.85rem 1rem' }}>
                                            <span style={{
                                                padding: '4px 10px', background: 'var(--bg-main)',
                                                borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
                                                border: '1px solid var(--border)',
                                            }}>
                                                {prod.category}
                                            </span>
                                        </td>

                                        {/* Stock Controls */}
                                        <td style={{ padding: '0.85rem 1rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                {stock === -1 ? (
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                        padding: '4px 16px', borderRadius: '10px',
                                                        background: '#eef2ff', border: '2px solid #c7d2fe',
                                                        height: '34px',
                                                    }}>
                                                        <InfinityIcon size={20} color="#6366f1" strokeWidth={2.5} />
                                                        <span style={{ fontWeight: 800, fontSize: '0.85rem', color: '#6366f1' }}>Sınırsız</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => handleUpdateStock(prod.id, stock - 1)}
                                                            style={{
                                                                width: '34px', height: '34px', borderRadius: '8px',
                                                                border: '2px solid #fecaca', background: '#fef2f2',
                                                                color: '#ef4444', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center',
                                                                cursor: 'pointer', transition: 'all 0.15s',
                                                                fontWeight: 800, fontSize: '1.1rem', lineHeight: 1,
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#ef4444'; }}
                                                            onMouseOut={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
                                                            title="-1"
                                                        >
                                                            −
                                                        </button>
                                                        <input
                                                            type="number"
                                                            value={stock}
                                                            onChange={(e) => handleUpdateStock(prod.id, e.target.value)}
                                                            style={{
                                                                width: '60px', textAlign: 'center',
                                                                background: 'var(--bg-card)', border: '2px solid var(--border)',
                                                                borderRadius: '10px', height: '34px', fontWeight: 800,
                                                                fontSize: '0.95rem', color: status.color,
                                                                outline: 'none', margin: '0 2px',
                                                            }}
                                                            onFocus={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                                                            onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                                                        />
                                                        <button
                                                            onClick={() => handleUpdateStock(prod.id, stock + 1)}
                                                            style={{
                                                                width: '34px', height: '34px', borderRadius: '8px',
                                                                border: '2px solid #bbf7d0', background: '#f0fdf4',
                                                                color: '#22c55e', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center',
                                                                cursor: 'pointer', transition: 'all 0.15s',
                                                                fontWeight: 800, fontSize: '1.1rem', lineHeight: 1,
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#22c55e'; }}
                                                            onMouseOut={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                                                            title="+1"
                                                        >
                                                            +
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => handleToggleUnlimited(prod.id)}
                                                    title={stock === -1 ? 'Sınırsız stoku kaldır' : 'Sınırsız stok yap'}
                                                    style={{
                                                        width: '34px', height: '34px', borderRadius: '8px',
                                                        border: stock === -1 ? '2px solid #6366f1' : '2px solid var(--border)',
                                                        background: stock === -1 ? '#eef2ff' : 'var(--bg-card)',
                                                        color: stock === -1 ? '#6366f1' : 'var(--text-muted)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: 'pointer', transition: 'all 0.15s',
                                                        marginLeft: '4px',
                                                    }}
                                                    onMouseOver={e => { if (stock !== -1) { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.background = '#eef2ff'; } }}
                                                    onMouseOut={e => { if (stock !== -1) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--bg-card)'; } }}
                                                >
                                                    <InfinityIcon size={16} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        </td>

                                        {/* Stock Bar */}
                                        <td style={{ padding: '0.85rem 1rem' }}>
                                            <div style={{
                                                width: '100%', height: '6px', background: 'var(--bg-main)',
                                                borderRadius: '3px', overflow: 'hidden',
                                            }}>
                                                <div style={{
                                                    width: `${barWidth}%`, height: '100%',
                                                    background: status.color,
                                                    borderRadius: '3px',
                                                    transition: 'width 0.3s ease, background 0.3s ease',
                                                }} />
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                padding: '4px 10px', borderRadius: '8px',
                                                background: status.bg, color: status.color,
                                                fontSize: '0.75rem', fontWeight: 700,
                                            }}>
                                                <StatusIcon size={13} />
                                                {status.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {paginatedProducts.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '3rem', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                            <Package size={40} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
                                            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                                {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz menüde ürün yok'}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{
                        padding: '1rem 1.5rem',
                        borderTop: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'var(--bg-main)',
                    }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)} / {filteredProducts.length} ürün
                        </span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '6px 10px', borderRadius: '8px',
                                    border: '1px solid var(--border)', background: 'var(--bg-card)',
                                    color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-main)',
                                    cursor: currentPage === 1 ? 'default' : 'pointer',
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        style={{
                                            width: '32px', height: '32px', borderRadius: '8px',
                                            border: currentPage === pageNum ? '1px solid var(--primary)' : '1px solid var(--border)',
                                            background: currentPage === pageNum ? 'var(--primary)' : 'var(--bg-card)',
                                            color: currentPage === pageNum ? 'white' : 'var(--text-main)',
                                            cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '6px 10px', borderRadius: '8px',
                                    border: '1px solid var(--border)', background: 'var(--bg-card)',
                                    color: currentPage === totalPages ? 'var(--text-muted)' : 'var(--text-main)',
                                    cursor: currentPage === totalPages ? 'default' : 'pointer',
                                    opacity: currentPage === totalPages ? 0.5 : 1,
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Stock Log Modal */}
            {showLogModal && (
                <div className="profile-modal-overlay" onClick={() => setShowLogModal(false)} style={{ zIndex: 9999 }}>
                    <div className="card glass shadow-2xl" style={{
                        maxWidth: '600px', width: '100%', padding: 0,
                        overflow: 'hidden', border: 'none', borderRadius: '24px',
                        maxHeight: '80vh',
                    }} onClick={e => e.stopPropagation()}>
                        {/* Log Header */}
                        <div style={{
                            padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '12px',
                                    background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <History size={20} />
                                </div>
                                <div>
                                    <h3 style={{ fontWeight: 800, fontSize: '1.05rem', margin: 0 }}>Değişiklik Geçmişi</h3>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Bu oturumdaki stok değişiklikleri</p>
                                </div>
                            </div>
                            <button onClick={() => setShowLogModal(false)} style={{
                                background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%',
                                width: '32px', height: '32px', cursor: 'pointer', color: 'var(--text-main)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <X size={16} />
                            </button>
                        </div>

                        {/* Log Content */}
                        <div style={{ padding: '1rem 1.5rem', overflowY: 'auto', maxHeight: 'calc(80vh - 100px)' }}>
                            {stockLog.length === 0 ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    Henüz değişiklik yok
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {stockLog.map((log, i) => (
                                        <div key={i} style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '10px 14px', borderRadius: '12px',
                                            background: 'var(--bg-main)', border: '1px solid var(--border)',
                                        }}>
                                            <div style={{
                                                width: '32px', height: '32px', borderRadius: '8px',
                                                background: log.change > 0 ? '#f0fdf4' : '#fef2f2',
                                                color: log.change > 0 ? '#22c55e' : '#ef4444',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 800, fontSize: '0.75rem', flexShrink: 0,
                                            }}>
                                                {log.change > 0 ? `+${log.change}` : log.change}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {log.productName}
                                                </div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                    {log.category} · {log.oldStock} → {log.newStock}
                                                </div>
                                            </div>
                                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                                                {log.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;
